/**
 * Create Plan Tool
 * 
 * Generates initial presentation structure with sections and slides.
 * Automatically assigns unique IDs to all sections and slides.
 * 
 * Use this ONLY for initial plan creation. For modifications, use editPlan.
 */

import { ToolDescription, safeParseToolParams } from "../../../base/toolDescription";
import { ToolBase } from "../../../base/toolBase";
import { normalizePlan, type Plan } from "../types";
import {
    CreatePlanParamsSchema,
    type CreatePlanParams,
    type CreatePlanResponse,
} from "./createPlan.schema";
import { CREATE_PLAN_TOOL_DESCRIPTION } from "./createPlan.prompt";

// ═══════════════════════════════════════════════════════════════════════════
// CREATE PLAN TOOL
// ═══════════════════════════════════════════════════════════════════════════

// System prompt fallback for Keywords AI
const CREATE_PLAN_SYSTEM_PROMPT = `### createPlan
Create a new presentation plan with sections and slides.

**When to use:**
- Creating a new presentation from scratch
- User describes what they want in a deck
- After gathering requirements with userInterview (if needed)

**IMPORTANT:** You MUST call this tool to create plans - don't just describe them.

**Guidelines:**
- Create logical section groupings (typically 3-8 sections)
- Distribute slides appropriately (avoid 1-slide or 20+ slide sections)
- Write clear, specific slide purposes
- Consider standard archetypes: board deck, pitch deck, QBR, investor update

\`\`\`json
{
  "targetSlideCount": 15,
  "sections": [
    {
      "title": "Executive Summary",
      "purpose": "High-level overview and key takeaways",
      "slides": [
        { "title": "Key Highlights", "purpose": "Show top 3-5 metrics with QoQ comparison" }
      ]
    }
  ]
}
\`\`\``;

/**
 * Tool for creating presentation plans
 */
export class CreatePlanTool extends ToolBase<CreatePlanParams, CreatePlanResponse> {
    public readonly name = "createPlan";
    public readonly description: ToolDescription;
    
    // System prompt description
    public readonly systemPromptDescription = CREATE_PLAN_SYSTEM_PROMPT;

    // Streaming operation fields
    fieldsToCheck = [
        { name: "targetSlideCount", type: "number" as const, list_status: false, order: 1 },
    ];

    listsToCheck = [
        {
            name: "sections",
            type: "array" as const,
            item_type: "object" as const,
            list_status: true,
            order: 2,
            check_sub_field: "title", // Section is ready when title is present
        },
    ];

    constructor() {
        super();
        this.description = new ToolDescription(
            "createPlan",
            CREATE_PLAN_TOOL_DESCRIPTION,
            CreatePlanParamsSchema
        );
    }

    public async execute(parameters: unknown): Promise<CreatePlanResponse> {
        console.log('[CreatePlanTool] 🔧 Executing with params:', JSON.stringify(parameters, null, 2));

        // Validate parameters
        const parseResult = safeParseToolParams(CreatePlanParamsSchema, parameters);

        if (!parseResult.success) {
            throw new Error(`[CreatePlanTool] Validation failed: ${parseResult.error}`);
        }

        const params = parseResult.data;

        try {
            // Create the plan structure
            const rawPlan: Plan = {
                sections: params.sections
            };

            // Normalize plan: add IDs, indices, and counts
            const normalizedPlan = normalizePlan(rawPlan);

            // Calculate variance from target if provided
            const variance = params.targetSlideCount
                ? normalizedPlan.totalSlides! - params.targetSlideCount
                : undefined;

            // Build response message
            let message = `Created ${normalizedPlan.totalSections}-section plan with ${normalizedPlan.totalSlides} slides.`;

            if (params.targetSlideCount && variance !== undefined) {
                if (Math.abs(variance) > 5) {
                    message += ` (Target was ${params.targetSlideCount} slides, ${variance > 0 ? '+' : ''}${variance} variance to accommodate logical structure)`;
                }
            }

            console.log(`[CreatePlanTool] ✅ Successfully created plan: ${normalizedPlan.totalSections} sections, ${normalizedPlan.totalSlides} slides`);

            return {
                success: true,
                plan: normalizedPlan,
                message,
                metadata: {
                    targetSlideCount: params.targetSlideCount,
                    actualSlideCount: normalizedPlan.totalSlides!,
                    variance
                }
            };

        } catch (error) {
            console.error('[CreatePlanTool] ❌ Error creating plan:', error);
            throw new Error(`Failed to create plan: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }
}

export default CreatePlanTool;

